/**
 * Row model for the pipeline activity & audit trail (SE-5504).
 *
 * Pure helpers, deliberately free of React so the audit-trail invariants can be
 * asserted directly in unit tests:
 *   - EVERY entry the server returns becomes a row. The ticket's Out of Scope
 *     forbids client-side re-filtering or hiding — this module never drops an
 *     entry, not even one whose `action` it has no bespoke phrasing for (the
 *     enum fallback prints the raw action rather than swallowing the row, so a
 *     backend that starts emitting a new action surfaces it instead of hiding
 *     it).
 *   - Rows are ordered newest-first. Ordering is NOT filtering: the sort is
 *     defensive because `PipelineActivityLog` encodes no `orderBy`, exactly as
 *     `selectPinnedNote` sorts rather than trusting `edges[0]`.
 *   - An entry whose `actorType` is not `USER` is flagged `isAutomated`, which
 *     is what AC2's automated-vs-manual marker renders from.
 *
 * Lives in `features/player-kanban/utils` (not next to the component) because
 * component directories are restricted to index/Container/View files by the
 * project's component-structure lint rule. Mirrors `reviewQueueRows.ts`.
 * @module features/player-kanban/utils/pipelineActivityRows
 */
import {
  KanbanCardOutcome,
  PipelineActivityAction,
  type PipelineActivityEntryFragmentFragment,
  PipelineActorType,
} from "@/generated/graphql";

import { formatPreviewDate } from "./playerPreviewModel";

/** One audit-trail entry, shaped for the renderer. */
export interface PipelineActivityRow {
  /** Activity entry id — the row identity. */
  readonly id: string;
  /** The raw action, kept so consumers can key behaviour off it. */
  readonly action: PipelineActivityAction;
  /** Human sentence describing what happened ("Moved from X to Y"). */
  readonly description: string;
  /**
   * Stage the card left, when the entry records one. Kept ALONGSIDE
   * `description` rather than replacing it: the manager log renders the move as
   * an arrow pair ("Evaluation → Negotiation") while the card-scoped trail keeps
   * the sentence, and both read the same entry.
   */
  readonly fromPhaseName: string | null;
  /** Stage the card entered, when the entry records one. */
  readonly toPhaseName: string | null;
  /** The player the entry concerns; null when the server sent no name. */
  readonly playerName: string | null;
  /**
   * Who did it (SE-6864). The automation RULE's name for a rule-driven entry,
   * the resolved human name for a manual one, and the actor-type label
   * ("Automation" / "System" / "Team member") when neither can be resolved.
   * Never empty — see {@link resolveActorLabel}.
   */
  readonly actorLabel: string;
  /**
   * Whether this entry was produced by something other than a human — the
   * AC2 signal. `AUTOMATION_RULE` and `SYSTEM` both count as automated; only
   * `USER` is manual.
   */
  readonly isAutomated: boolean;
  /** Actor-type label, always the TYPE (never a person's name). */
  readonly actorTypeLabel: string;
  /** Raw ISO timestamp, kept for sorting and for assertions. */
  readonly occurredAt: string;
  /** Formatted timestamp for display. */
  readonly occurredAtLabel: string;
  /** The reason the mover typed into the sensitive-stage prompt, if any. */
  readonly rationale: string | null;
}

/** Actor type → display label. */
export const ACTOR_TYPE_LABEL: Record<PipelineActorType, string> = {
  [PipelineActorType.AutomationRule]: "Automation",
  [PipelineActorType.System]: "System",
  [PipelineActorType.User]: "Team member",
};

/** Terminal outcome → display label. */
const OUTCOME_LABEL: Record<KanbanCardOutcome, string> = {
  [KanbanCardOutcome.Passed]: "Passed",
  [KanbanCardOutcome.Promoted]: "Promoted",
  [KanbanCardOutcome.Returned]: "Returned",
  [KanbanCardOutcome.Signed]: "Signed",
  [KanbanCardOutcome.Sold]: "Sold",
  [KanbanCardOutcome.Withdrawn]: "Withdrawn",
};

/**
 * Phrasing for the actions whose description needs no entry context.
 * `ADDED`, `MOVED`, `CLOSED` and `REOPENED` are absent because each reads
 * stage/outcome fields off the entry — see {@link describeActivityAction}.
 */
const STATIC_ACTION_LABEL: Partial<Record<PipelineActivityAction, string>> = {
  [PipelineActivityAction.NotesUpdated]: "Notes updated",
  [PipelineActivityAction.Removed]: "Removed from the pipeline",
  [PipelineActivityAction.ReportAdded]: "Scout report added",
  [PipelineActivityAction.ReviewAccepted]: "Review accepted",
  [PipelineActivityAction.ReviewDeclined]: "Review declined",
  [PipelineActivityAction.ReviewRequested]: "Review requested",
  [PipelineActivityAction.TagApplied]: "Tag applied",
  [PipelineActivityAction.TagRemoved]: "Tag removed",
};

/** Printed in place of a stage name the server left null (a deleted stage). */
const UNKNOWN_STAGE = "an unknown stage";

/**
 * Turn a raw enum value into readable words, e.g. `TAG_APPLIED` → `Tag
 * applied`. Only ever reached for an action or actor type this build predates;
 * keeping the row visible and attributed (rather than dropping it, or printing
 * nothing) is what the "render exactly what it receives" constraint requires.
 * @param value - The unrecognised enum value.
 * @returns A sentence-cased rendering of the raw enum value.
 */
const humaniseEnumValue = (value: string): string => {
  const words = String(value).toLowerCase().replace(/_/g, " ");
  return words.charAt(0).toUpperCase() + words.slice(1);
};

/**
 * The actor-TYPE label for an entry — always the kind of actor, never a name.
 *
 * Falls through {@link humaniseEnumValue} for an actor type this build
 * predates, so the label is never `undefined`: the SE-6864 AC is that NO entry
 * renders unattributed, and a new backend enum value must degrade to readable
 * words rather than to a blank line.
 * @param actorType - The entry's actor type.
 * @returns The type label; always a non-empty string.
 */
const resolveActorTypeLabel = (actorType: PipelineActorType): string => {
  // Annotated `| undefined` on purpose: the Record is exhaustive over the enum
  // as this build knows it, but the VALUE arrives from the server and can be a
  // member this build predates, exactly as `describeActivityAction` assumes for
  // actions.
  const label: string | undefined = ACTOR_TYPE_LABEL[actorType];
  return label ?? humaniseEnumValue(actorType);
};

/**
 * Describe what an activity entry records, in one sentence.
 * @param entry - The activity entry.
 * @returns The human description.
 */
export const describeActivityAction = (
  entry: PipelineActivityEntryFragmentFragment
): string => {
  const from = entry.fromPhaseName?.trim() || UNKNOWN_STAGE;
  const to = entry.toPhaseName?.trim() || UNKNOWN_STAGE;

  switch (entry.action) {
    case PipelineActivityAction.Added:
      return entry.toPhaseName?.trim()
        ? `Added to ${to}`
        : "Added to the pipeline";
    case PipelineActivityAction.Moved:
      return `Moved from ${from} to ${to}`;
    case PipelineActivityAction.Closed:
      return entry.outcome
        ? `Closed as ${OUTCOME_LABEL[entry.outcome]}`
        : "Closed";
    case PipelineActivityAction.Reopened:
      return entry.toPhaseName?.trim() ? `Reopened into ${to}` : "Reopened";
    default:
      return (
        STATIC_ACTION_LABEL[entry.action] ?? humaniseEnumValue(entry.action)
      );
  }
};

/**
 * Format an audit timestamp for display.
 *
 * Reuses `formatPreviewDate` (the drawer's existing date format) and appends a
 * UTC clock time, because an audit trail without a time-of-day cannot order two
 * moves made on the same day. UTC — not the device zone — so the rendered value
 * is deterministic across machines and CI.
 * @param iso - The ISO timestamp.
 * @returns e.g. `20 Jun 2026 · 14:32`, or an em dash when unparseable.
 */
export const formatActivityTimestamp = (
  iso: string | null | undefined
): string => {
  const datePart = formatPreviewDate(iso);
  if (!datePart || !iso) return "—";
  const parsed = new Date(iso);
  const hours = String(parsed.getUTCHours()).padStart(2, "0");
  const minutes = String(parsed.getUTCMinutes()).padStart(2, "0");
  return `${datePart} · ${hours}:${minutes}`;
};

/**
 * Whether an entry was produced by something other than a human (AC2).
 * @param actorType - The entry's actor type.
 * @returns `true` for `AUTOMATION_RULE` and `SYSTEM`, `false` for `USER`.
 */
export const isAutomatedActor = (actorType: PipelineActorType): boolean =>
  actorType !== PipelineActorType.User;

/**
 * Name the actor behind an entry (SE-6864).
 *
 * Three cases, in the order the schema makes them decidable:
 *
 * - **`AUTOMATION_RULE`** — the rule's own name, which the backend snapshots
 *   onto the entry at event time (`automationRuleName` is documented non-null
 *   exactly for this actor type, and it is a SNAPSHOT, so renaming or deleting
 *   the rule later does not rewrite history). A rule-driven entry never borrows
 *   `actorUserId`, even when one is present: the human who authored the rule is
 *   not the actor who fired it.
 * - **`USER`** — the roster-resolved display name.
 * - **`SYSTEM`** — the type label; a migration or cron has no other identity.
 *
 * ## The unattributed-USER case, deliberately
 *
 * `actorUserId` is documented as "null means the actor was not threaded
 * through — NOT that the action was automated", and an id absent from the org
 * roster (a departed member) is the same situation one step later. Both fall
 * back to the actor-TYPE label, `Team member`, which is the strongest true
 * statement available: it attributes the entry to a human without inventing
 * WHICH human, and it can never be mistaken for the automation label. The AC's
 * "no entry renders without attribution" is met — the label is never empty —
 * while a fabricated name would be a worse failure than a vague honest one.
 * @param entry - The activity entry fragment.
 * @param actorNames - `userId` → display name, for human actors.
 * @returns The label to render; always a non-empty string.
 */
export const resolveActorLabel = (
  entry: PipelineActivityEntryFragmentFragment,
  actorNames: ReadonlyMap<string, string> = new Map()
): string => {
  const actorTypeLabel = resolveActorTypeLabel(entry.actorType);

  if (entry.actorType === PipelineActorType.AutomationRule) {
    return entry.automationRuleName?.trim() || actorTypeLabel;
  }
  if (entry.actorType === PipelineActorType.User) {
    const resolvedName = entry.actorUserId
      ? actorNames.get(entry.actorUserId)?.trim()
      : undefined;
    return resolvedName || actorTypeLabel;
  }
  return actorTypeLabel;
};

/**
 * Build one row from an activity entry.
 * @param entry - The activity entry fragment.
 * @param actorNames - `userId` → display name, for human actors.
 * @returns The renderer-ready row.
 */
const toRow = (
  entry: PipelineActivityEntryFragmentFragment,
  actorNames: ReadonlyMap<string, string>
): PipelineActivityRow => {
  const actorTypeLabel = resolveActorTypeLabel(entry.actorType);

  return {
    id: entry.id,
    action: entry.action,
    description: describeActivityAction(entry),
    fromPhaseName: entry.fromPhaseName?.trim() || null,
    toPhaseName: entry.toPhaseName?.trim() || null,
    playerName: entry.playerName?.trim() || null,
    actorLabel: resolveActorLabel(entry, actorNames),
    isAutomated: isAutomatedActor(entry.actorType),
    actorTypeLabel,
    occurredAt: entry.occurredAt,
    occurredAtLabel: formatActivityTimestamp(entry.occurredAt),
    rationale: entry.rationale?.trim() || null,
  };
};

/**
 * Map activity entries to rows, newest-first.
 *
 * De-duplicates by id (paging can re-deliver a boundary entry) but NEVER drops
 * an entry for any other reason — see the module docblock.
 * @param entries - Activity entries from `pipelineActivityLog`.
 * @param actorNames - `userId` → display name, for human actors.
 * @returns One row per unique entry id, ordered newest-first.
 */
export const buildPipelineActivityRows = (
  entries: readonly PipelineActivityEntryFragmentFragment[],
  actorNames: ReadonlyMap<string, string> = new Map()
): PipelineActivityRow[] => {
  const byId = new Map(entries.map(entry => [entry.id, entry]));
  return [...byId.values()]
    .map(entry => toRow(entry, actorNames))
    .sort((a, b) => b.occurredAt.localeCompare(a.occurredAt));
};
